import React, { useState } from 'react';
import { Container, Row, Col, Card, Table, ProgressBar, Button, Modal } from 'react-bootstrap';
import { TrendingUp, Star, CheckCircle, Download, Trophy, Lock, Eye, Building, Calendar, Hash, FileText, Printer, Award, Mail, Phone, Image as ImageIcon, ExternalLink } from 'lucide-react';
import '../../../assets/css/donor/dashboard/MyRewards.css';
import DonorLayout from '../../../layouts/donor/DonorLayout';

const MyRewards = () => {
  const [myrActiveFilter, setMyrActiveFilter] = useState('all');
  const [myrShowModal, setMyrShowModal] = useState(false);
  const [myrSelected, setMyrSelected] = useState(null);
  const [myrShowSlipModal, setMyrShowSlipModal] = useState(false);

  const myrDonorData = { 
    totalDonations: 75000, 
    currentLevel: 'Silver', 
    totalRewards: 2, 
    nextLevel: 'Gold', 
    nextLevelAmount: 150000, 
    progressPercent: 50 
  };

  const myrRewardsData = [
    { 
      id: 1, 
      ngo: 'Helping NGO A', 
      ngoSub: 'Education Support', 
      ngoAddress: 'Lahore, Punjab, Pakistan', 
      ngoContact: '+92 42 1234567', 
      ngoEmail: 'info@helpingngo.org', 
      level: 'Bronze', 
      reward: 'Badge', 
      rewardIcon: '🏅', 
      amount: 25000, 
      date: '12 Feb 2024', 
      slipNo: 'SLP-2024-001', 
      status: 'Issued', 
      slipNote: 'Awarded for reaching Bronze level — Thank you for your generous contribution to education.',
      slipImage: 'https://via.placeholder.com/800x600/10183f/ffffff?text=NGO+Reward+Slip+Certificate' 
    },
    { 
      id: 2, 
      ngo: 'Charity Org B', 
      ngoSub: 'Student Welfare', 
      ngoAddress: 'Karachi, Sindh, Pakistan', 
      ngoContact: '+92 21 9876543', 
      ngoEmail: 'support@charityorgb.org', 
      level: 'Silver', 
      reward: 'Certificate', 
      rewardIcon: '📜', 
      amount: 50000, 
      date: '05 Jan 2024', 
      slipNo: 'SLP-2024-002', 
      status: 'Issued', 
      slipNote: 'Congratulations on reaching Silver level — Your support changes student lives.',
      slipImage: 'https://via.placeholder.com/800x600/ff8c00/ffffff?text=Silver+Level+Certificate' 
    },
    { 
      id: 3, 
      ngo: 'Support Fund C', 
      ngoSub: 'School Fees Aid', 
      ngoAddress: 'Islamabad, Pakistan', 
      ngoContact: '+92 51 5551234', 
      ngoEmail: 'help@supportfundc.org', 
      level: 'Silver', 
      reward: 'Certificate', 
      rewardIcon: '📜', 
      amount: 21000, 
      date: '18 Dec 2023', 
      slipNo: 'SLP-2023-047', 
      status: 'Issued', 
      slipNote: 'Silver level reward for consistent school fee donations across multiple students.',
      slipImage: 'https://via.placeholder.com/800x600/10183f/ffffff?text=Reward+Slip+Document' 
    },
    { 
      id: 4, 
      ngo: 'EduCare NGO', 
      ngoSub: 'Book Fund', 
      ngoAddress: 'Faisalabad, Punjab, Pakistan', 
      ngoContact: '+92 41 8008800', 
      ngoEmail: 'rewards@educarengo.org', 
      level: 'Gold', 
      reward: 'Trophy', 
      rewardIcon: '🏆', 
      amount: 80000, 
      date: '10 Nov 2023', 
      slipNo: 'SLP-2023-031', 
      status: 'Pending', 
      slipNote: 'Gold level reward pending verification — Our team will issue your trophy within 7 working days.',
      slipImage: null 
    },
  ];

  const myrLevelSteps = [
    { name: 'Bronze', emoji: '🥉', amount: 'PKR 0+', state: 'done' },
    { name: 'Silver', emoji: '🥈', amount: 'PKR 50,000', state: 'current' },
    { name: 'Gold', emoji: '🥇', amount: 'PKR 150,000', state: 'locked' },
    { name: 'Platinum', emoji: '💎', amount: 'PKR 300,000', state: 'locked' },
  ];

  const myrFiltered = myrRewardsData.filter((r) => myrActiveFilter === 'all' || r.level.toLowerCase() === myrActiveFilter);

  const getMyrLevelClass = (level) => ({ 
    Bronze: 'myr-level-bronze', 
    Silver: 'myr-level-silver', 
    Gold: 'myr-level-gold', 
    Platinum: 'myr-level-platinum' 
  }[level] || 'myr-level-silver');

  const handleMyrView = (r) => { 
    setMyrSelected(r); 
    setMyrShowModal(true); 
  };

  const handleMyrViewSlip = () => {
    setMyrShowModal(false);
    setMyrShowSlipModal(true);
  };

  const handleMyrDownloadSlip = () => {
    if (myrSelected && myrSelected.slipImage) {
      const link = document.createElement('a');
      link.href = myrSelected.slipImage;
      link.download = `${myrSelected.slipNo}_Reward_Slip.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleMyrExport = () => {
    alert('Exporting rewards report...');
  };

  const handleMyrPrint = () => {
    window.print();
  };

  return (
    <DonorLayout>
      <div className="myr-container">
        <Container fluid>

          {/* Page Header */}
          <div className="myr-page-header">
            <div className="myr-page-title-wrap">
              <h2>My Rewards</h2>
              <p>Track your donation milestones and earned rewards</p>
            </div>
            <Button className="myr-export-btn" onClick={handleMyrExport}>
              <Download size={16} /> Export Report
            </Button>
          </div>

          {/* Stats Cards */}
          <Row className="g-3 mb-4">
            <Col xl={3} lg={6} md={6} sm={6}>
              <Card className="myr-stat-card">
                <Card.Body>
                  <div className="myr-stat-icon-row">
                    <div className="myr-stat-icon myr-icon-navy">
                      <TrendingUp size={18} />
                    </div>
                    <p className="myr-stat-label">Total Donations</p>
                  </div>
                  <p className="myr-stat-value myr-stat-value-orange">
                    PKR {myrDonorData.totalDonations.toLocaleString()}
                  </p>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={3} lg={6} md={6} sm={6}>
              <Card className="myr-stat-card">
                <Card.Body>
                  <div className="myr-stat-icon-row">
                    <div className="myr-stat-icon myr-icon-green">
                      <CheckCircle size={18} />
                    </div>
                    <p className="myr-stat-label">Current Level</p>
                  </div>
                  <div className="myr-level-badge-wrap">
                    <CheckCircle size={18} className="myr-level-check" />
                    <span className="myr-level-badge">{myrDonorData.currentLevel}</span>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={3} lg={6} md={6} sm={6}>
              <Card className="myr-stat-card">
                <Card.Body>
                  <div className="myr-stat-icon-row">
                    <div className="myr-stat-icon myr-icon-orange">
                      <Trophy size={18} />
                    </div>
                    <p className="myr-stat-label">Total Rewards</p>
                  </div>
                  <p className="myr-stat-value">{myrDonorData.totalRewards}</p>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={3} lg={6} md={6} sm={6}>
              <Card className="myr-stat-card">
                <Card.Body>
                  <div className="myr-stat-icon-row">
                    <div className="myr-stat-icon myr-icon-gold">
                      <Star size={18} />
                    </div>
                    <p className="myr-stat-label">Next Level Progress</p>
                  </div>
                  <div className="myr-progress-label-row">
                    <span className="myr-progress-next">{myrDonorData.nextLevel}</span>
                    <span className="myr-progress-pct">{myrDonorData.progressPercent}%</span>
                  </div>
                  <div className="myr-progress-bar-wrap">
                    <ProgressBar now={myrDonorData.progressPercent} />
                  </div>
                  <p className="myr-progress-goal-text">
                    PKR {(myrDonorData.nextLevelAmount - myrDonorData.totalDonations).toLocaleString()} more to reach {myrDonorData.nextLevel}
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Rewards Table */}
          <Row className="mb-4">
            <Col>
              <Card className="myr-table-card">
                <div className="myr-table-header-row">
                  <h5 className="myr-table-title">
                    <span className="myr-table-title-dot" />
                    Rewards History
                    <span className="myr-table-count-badge">{myrFiltered.length} records</span>
                  </h5>
                  <div className="myr-filter-tabs">
                    {['all','bronze','silver','gold','platinum'].map((tab) => (
                      <button 
                        key={tab} 
                        className={`myr-filter-tab ${myrActiveFilter === tab ? 'active' : ''}`} 
                        onClick={() => setMyrActiveFilter(tab)}
                      >
                        {tab === 'all' ? 'All' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="myr-table mb-0">
                      <thead className="myr-table-head">
                        <tr>
                          <th>NGO</th>
                          <th>Level</th>
                          <th>Reward</th>
                          <th>Amount</th>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {myrFiltered.length > 0 ? myrFiltered.map((row) => (
                          <tr key={row.id} className="myr-table-row">
                            <td>
                              <div className="myr-ngo-name">{row.ngo}</div>
                              <div className="myr-ngo-sub">{row.ngoSub}</div>
                            </td>
                            <td>
                              <span className={`myr-level-pill ${getMyrLevelClass(row.level)}`}>
                                {row.level}
                              </span>
                            </td>
                            <td>
                              <span className="myr-reward-type">{row.rewardIcon} {row.reward}</span>
                            </td>
                            <td>
                              <span className="myr-amount-text">PKR {row.amount.toLocaleString()}</span>
                            </td>
                            <td>
                              <span className="myr-date-text">{row.date}</span>
                            </td>
                            <td>
                              {row.status === 'Issued' ? (
                                <span className="myr-status-issued">
                                  <CheckCircle size={12} /> Issued
                                </span>
                              ) : (
                                <span className="myr-status-pending">⏳ Pending</span>
                              )}
                            </td>
                            <td>
                              <Button 
                                className="myr-view-btn" 
                                size="sm" 
                                onClick={() => handleMyrView(row)}
                              >
                                <Eye size={14} /> View
                              </Button>
                            </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan="7">
                              <div className="myr-empty-state">
                                <div className="myr-empty-icon">
                                  <Trophy size={30} />
                                </div>
                                <p className="myr-empty-text">No rewards found for this level.</p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Level Journey */}
          <Row>
            <Col>
              <Card className="myr-journey-card">
                <Card.Body>
                  <h5 className="myr-journey-title">🎯 Your Level Journey</h5>
                  <p className="myr-journey-sub">Keep donating to unlock higher levels and exclusive rewards</p>
                  <div className="myr-journey-steps">
                    {myrLevelSteps.map((step, idx) => (
                      <div key={idx} className="myr-journey-step">
                        <div className={`myr-step-circle ${
                          step.state === 'done' ? 'myr-step-done' : 
                          step.state === 'current' ? 'myr-step-current' : 
                          'myr-step-locked'
                        }`}>
                          {step.state === 'locked' ? <Lock size={16} /> : step.emoji}
                        </div>
                        <span className={`myr-step-name ${step.state === 'current' ? 'myr-step-name-active' : ''}`}>
                          {step.name}
                        </span>
                        <span className="myr-step-amount">{step.amount}</span>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

        </Container>
      </div>

      {/* Detail View Modal */}
      {myrSelected && (
        <Modal 
          show={myrShowModal} 
          onHide={() => setMyrShowModal(false)} 
          centered 
          size="lg" 
          className="myr-modal"
        >
          <Modal.Header closeButton className="myr-modal-header">
            <Modal.Title className="myr-modal-title">
              <Award size={22} className="me-2" />
              Reward Details
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="myr-modal-body">
            
            {/* Reward Info Card */}
            <div className="myr-detail-card">
              <div className="myr-detail-header">
                <div className="myr-detail-icon">{myrSelected.rewardIcon}</div>
                <div className="myr-detail-header-info">
                  <h4 className="myr-detail-reward-title">{myrSelected.reward}</h4>
                  <span className={`myr-level-pill ${getMyrLevelClass(myrSelected.level)}`}>
                    {myrSelected.level} Level
                  </span>
                </div>
                <div className={`myr-detail-status ${myrSelected.status === 'Issued' ? 'myr-detail-issued' : 'myr-detail-pending'}`}>
                  {myrSelected.status === 'Issued' ? (
                    <><CheckCircle size={14} /> Issued</>
                  ) : (
                    <>⏳ Pending</>
                  )}
                </div>
              </div>

              <div className="myr-detail-divider"></div>

              {/* NGO Details */}
              <div className="myr-detail-section">
                <h5 className="myr-detail-section-title">
                  <Building size={18} /> NGO Information
                </h5>
                <Row className="g-3">
                  <Col md={6}>
                    <div className="myr-detail-item">
                      <span className="myr-detail-label">Organization Name</span>
                      <span className="myr-detail-value">{myrSelected.ngo}</span>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="myr-detail-item">
                      <span className="myr-detail-label">Category</span>
                      <span className="myr-detail-value">{myrSelected.ngoSub}</span>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="myr-detail-item">
                      <span className="myr-detail-label">
                        <Building size={14} /> Address
                      </span>
                      <span className="myr-detail-value">{myrSelected.ngoAddress}</span>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="myr-detail-item">
                      <span className="myr-detail-label">
                        <Phone size={14} /> Contact
                      </span>
                      <span className="myr-detail-value">{myrSelected.ngoContact}</span>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="myr-detail-item">
                      <span className="myr-detail-label">
                        <Mail size={14} /> Email
                      </span>
                      <span className="myr-detail-value">{myrSelected.ngoEmail}</span>
                    </div>
                  </Col>
                </Row>
              </div>

              <div className="myr-detail-divider"></div>

              {/* Reward Details */}
              <div className="myr-detail-section">
                <h5 className="myr-detail-section-title">
                  <Award size={18} /> Reward Information
                </h5>
                <Row className="g-3">
                  <Col md={6}>
                    <div className="myr-detail-item">
                      <span className="myr-detail-label">
                        <Hash size={14} /> Slip Number
                      </span>
                      <span className="myr-detail-value myr-detail-mono">{myrSelected.slipNo}</span>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="myr-detail-item">
                      <span className="myr-detail-label">
                        <Calendar size={14} /> Issue Date
                      </span>
                      <span className="myr-detail-value">{myrSelected.date}</span>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="myr-detail-item">
                      <span className="myr-detail-label">Donation Amount</span>
                      <span className="myr-detail-value myr-detail-amount">
                        PKR {myrSelected.amount.toLocaleString()}
                      </span>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="myr-detail-note">
                      <FileText size={16} />
                      <p>{myrSelected.slipNote}</p>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* NGO Slip Section */}
              {myrSelected.slipImage && (
                <>
                  <div className="myr-detail-divider"></div>
                  <div className="myr-detail-section">
                    <h5 className="myr-detail-section-title">
                      <ImageIcon size={18} /> NGO Reward Slip
                    </h5>
                    <div className="myr-slip-preview-box">
                      <div className="myr-slip-info-text">
                        <ImageIcon size={20} />
                        <div>
                          <p className="myr-slip-info-title">Reward Slip Uploaded</p>
                          <p className="myr-slip-info-desc">
                            NGO has uploaded the official reward slip. Click to view or download.
                          </p>
                        </div>
                      </div>
                      <div className="myr-slip-action-btns">
                        <Button className="myr-slip-view-btn" onClick={handleMyrViewSlip}>
                          <ExternalLink size={16} /> View Slip
                        </Button>
                        <Button className="myr-slip-download-btn" onClick={handleMyrDownloadSlip}>
                          <Download size={16} /> Download
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {!myrSelected.slipImage && myrSelected.status === 'Pending' && (
                <>
                  <div className="myr-detail-divider"></div>
                  <div className="myr-detail-section">
                    <div className="myr-slip-pending-box">
                      <ImageIcon size={24} />
                      <p className="myr-slip-pending-text">
                        Reward slip will be uploaded by NGO once the reward is issued.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>

          </Modal.Body>

          <Modal.Footer className="myr-modal-footer">
            <Button className="myr-modal-close-btn" onClick={() => setMyrShowModal(false)}>
              Close
            </Button>
            {/* <Button className="myr-modal-print-btn" onClick={handleMyrPrint}>
              <Printer size={16} /> Print Details
            </Button> */}
          </Modal.Footer>
        </Modal>
      )}

      {/* Slip View Modal */}
      {myrSelected && (
        <Modal 
          show={myrShowSlipModal} 
          onHide={() => setMyrShowSlipModal(false)} 
          centered 
          size="xl" 
          className="myr-slip-modal"
        >
          <Modal.Header closeButton className="myr-slip-modal-header">
            <Modal.Title className="myr-slip-modal-title">
              <ImageIcon size={22} className="me-2" />
              Reward Slip - {myrSelected.slipNo}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="myr-slip-modal-body">
            <div className="myr-slip-image-container">
              {myrSelected.slipImage && (
                <img 
                  src={myrSelected.slipImage} 
                  alt="NGO Reward Slip" 
                  className="myr-slip-image"
                />
              )}
            </div>
            <div className="myr-slip-image-info">
              <p><strong>NGO:</strong> {myrSelected.ngo}</p>
              <p><strong>Reward:</strong> {myrSelected.reward}</p>
              <p><strong>Amount:</strong> PKR {myrSelected.amount.toLocaleString()}</p>
              <p><strong>Date:</strong> {myrSelected.date}</p>
            </div>
          </Modal.Body>

          <Modal.Footer className="myr-slip-modal-footer">
            <Button className="myr-slip-modal-back-btn" onClick={() => {
              setMyrShowSlipModal(false);
              setMyrShowModal(true);
            }}>
              Back to Details
            </Button>
            <Button className="myr-slip-modal-download-btn" onClick={handleMyrDownloadSlip}>
              <Download size={16} /> Download Slip
            </Button>
          </Modal.Footer>
        </Modal>
      )}

    </DonorLayout>
  );
};

export default MyRewards;